import { v4 } from 'uuid';
import { createLogger } from '@fabric-app/utils/logging';
import { isDefined } from '@fabric-app/utils/guards';

class TypedWorker {
  constructor(worker, name) {
    this.pendingRequests = /* @__PURE__ */ new Map();
    this.workerName = name ?? `TypedWorker-${v4()}`;
    this.worker = worker;
    this.worker.onmessage = this.handleMessage.bind(this);
    this.logger = createLogger(this.workerName, true);
  }
  handleMessage(event) {
    const { id, type, payload, error } = event.data;
    if (this.pendingRequests.has(id)) {
      const { resolve, reject, timeoutId } = this.pendingRequests.get(id);
      if (timeoutId) clearTimeout(timeoutId);
      if (error) {
        reject(new Error(error));
      } else {
        resolve(payload);
      }
      this.pendingRequests.delete(id);
    } else {
      if (type === "log") {
        this.logger.log(...Array.isArray(payload) ? payload : [
          payload
        ]);
      }
    }
  }
  postMessage(type, message, options) {
    const id = v4();
    return new Promise((resolve, reject) => {
      if (options?.signal?.aborted) {
        reject(new DOMException("The operation was aborted. (immediate)", "AbortError"));
        return;
      }
      let timeoutId;
      let abortHandler;
      if (options?.signal) {
        abortHandler = (reason) => {
          this.pendingRequests.delete(id);
          reject(new DOMException(`The operation was aborted. ${isDefined(reason) ? String(reason) : ""}`, "AbortError"));
          this.worker.postMessage({
            id,
            type: "__abort__",
            payload: isDefined(reason) ? String(reason) : void 0
          });
        };
        options.signal.addEventListener("abort", abortHandler);
      }
      if (options?.timeout) {
        timeoutId = setTimeout(() => {
          this.pendingRequests.delete(id);
          reject(new Error("Request timed out"));
          this.worker.postMessage({
            id,
            type: "__abort__",
            payload: "Request timed out"
          });
        }, options.timeout);
      }
      this.pendingRequests.set(id, {
        resolve,
        reject,
        timeoutId,
        abortHandler
      });
      this.worker.postMessage({
        id,
        type,
        payload: message
      });
    });
  }
  // Close the worker
  terminate() {
    this.worker.terminate();
    for (const { reject, timeoutId, abortHandler } of this.pendingRequests.values()) {
      if (timeoutId) clearTimeout(timeoutId);
      if (abortHandler) abortHandler("Worker terminated");
      reject(new Error("Worker terminated"));
    }
    this.pendingRequests.clear();
  }
  get hasPendingRequests() {
    return this.pendingRequests.size > 0;
  }
}

export { TypedWorker };
//# sourceMappingURL=TypedWorker.js.map
